<script setup lang="ts" generic="IncludePrimitive extends boolean = true, IncludeRelation extends boolean = true, Multiple extends boolean = false">
import type { RowSelectionState } from '@tanstack/vue-table';
import type { CollectionFieldSelectionProps, ModelValueBase } from '#layers/data-model/types/collection-field-selection';
import { createColumnHelper } from '@tanstack/vue-table';
import { computed, h } from 'vue';
import { useCollectionFieldSelection } from '#layers/data-model/composables/use-collection-field-selection';

type ModelValue = ModelValueBase<IncludePrimitive, IncludeRelation, Multiple>;

interface CollectionFieldListSlots {
	item?: (props: { item: any }) => any;
}

const props = withDefaults(defineProps<CollectionFieldSelectionProps<IncludePrimitive, IncludeRelation, Multiple>>(), {
	includePrimitiveFields: undefined,
	includeRelationFields: undefined,
});
const slots = defineSlots<CollectionFieldListSlots>();
const modelValue = defineModel<ModelValue>({ required: true });

const { items } = useCollectionFieldSelection(() => props, modelValue);

const columnHelper = createColumnHelper<any>();
const defaultLabelColumn = columnHelper.accessor('label', {
	header: 'Fields',
	cell: (options) => options.getValue(),
});

const itemColumn = columnHelper.display({
	id: 'item',
	header: 'Fields',
	enableHiding: false,
	enableResizing: false,
	size: 50,
	cell: ({ row }) => h('div', { class: 'flex w-full' }, [slots.item?.({ item: row.original })]),
});

const columns = [slots.item ? itemColumn : defaultLabelColumn];

function handleRowSelectionUpdate(rows: RowSelectionState) {
	modelValue.value = Object.keys(rows) as ModelValue;
}

const rowSelection = computed(() => {
	if (Array.isArray(modelValue.value)) {
		return modelValue.value?.reduce((acc: any, id) => {
			acc[id] = true;
			return acc;
		}, {}) ?? [];
	}
	else {
		return modelValue.value ? { [modelValue.value.toString()]: true } : {};
	}
});
</script>

<template>
	<DDataTable
		:data="items"
		:columns
		:disabled
		can-select
		can-multi-select
		hide-pagination
		:row-selection
		:get-row-id="row => row.id"
		@update:row-selection="handleRowSelectionUpdate"
	/>
</template>
